import { useState, useEffect, useRef, useCallback } from 'react';
import type { VisualComponent } from '@dtwin/shared';
import { fetchDataSource, subscribeDataSource, parseData, readStaticData } from '../services/dataProvider';
import { fetchDataSources } from '../api/dataSources';
import { useDeviceDataStore } from '../stores/deviceDataStore';
import useDashboardStore from '../stores/dashboardStore';
import { useViewerStore } from '../stores/viewerStore';
import { ensureDeviceSocket } from '../services/deviceSocket';

export interface WidgetDataState {
  data: unknown;
  loading: boolean;
  error: unknown;
}

/**
 * 数据绑定引擎：
 * - 引用数据集（dataSource.dataSetId）：按数据集 id 拉取定义（连接+解析），再取数据
 * - static：直读静态数据（走数据解析管线）
 * - api/sql/mqtt：拉取 + 按 refreshInterval 轮询
 * - websocket：原生 WebSocket 真流式订阅，每条消息到达即更新（含自动重连），数据解析管线处理
 * 统一执行：取值 → 字段映射 → 过滤 → JS 转换 数据解析管线。
 * 返回 `{ data, loading, error, refresh }`。
 */
export function useWidgetData(component: VisualComponent | null | undefined): WidgetDataState & { refresh: () => Promise<void> } {
  const ds = component?.dataSource;
  const dsKey = ds ? JSON.stringify(ds) : '';

  // device 类型：实时订阅设备数据中枢（阶段2｜数据同源），响应式跟进 deviceMap 变化
  const deviceLive = useDeviceDataStore((s) =>
    ds?.type === 'device' ? s.deviceMap[String((ds as any)?.config?.deviceId ?? '').trim()] : undefined
  );
  // 阶段3｜3.3 refresh 联动动作：signalRefresh(compId) → 触发本组件重拉
  const refreshTick = useDashboardStore((s) => s.refreshSignals[component?.id ?? ''] ?? 0);

  const [state, setState] = useState<WidgetDataState>(() => {
    const raw = readStaticData(ds);
    return { data: parseData(raw, (ds as any)?.parser), loading: false, error: null };
  });
  const abortRef = useRef<AbortController | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const runRef = useRef<(() => Promise<void>) | undefined>(undefined);
  /** 引用数据集的真实定义（含真实 type/config/parser），供流式订阅与轮询判断 */
  const dsDefRef = useRef<any>(null);

  const apply = useCallback((raw: unknown, parser?: any) => {
    // 组件级 parser 叠加：raw 已是数据集解析结果时，再用组件 parser 二次处理
    const compParser = parser || (ds as any)?.parser;
    setState({ data: parseData(raw, compParser), loading: false, error: null });
  }, [dsKey]);

  /** 应用数据集解析（数据集 parser 先执行）后，再叠加组件 parser */
  const applyWithSetParser = useCallback((raw: unknown) => {
    const set = dsDefRef.current;
    const parsed = set?.parser ? parseData(raw, set.parser) : raw;
    apply(parsed, (ds as any)?.parser);
  }, [apply, dsKey]);

  const run = useCallback(async () => {
    if (!ds) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    // DW-P0-3：发布快照静态化数据优先（数据已冻结；编辑器/实时态 staticData 为空 → 走实时链路）
    const frozenKey = component?.id ? `widget:${component.id}` : '';
    const frozen = frozenKey ? useViewerStore.getState().staticData?.[frozenKey] : undefined;
    if (frozen !== undefined) {
      setState({ data: parseData(frozen, (ds as any)?.parser), loading: false, error: null });
      return;
    }
    // device 类型：读设备数据中枢（无远端拉取，实时性由 deviceLive 订阅保证）；
    // points 并入 alarmList（告警计数/告警列表组件可直接用 parser 提取）
    if (ds.type === 'device') {
      const devId = String((ds as any)?.config?.deviceId ?? '').trim();
      const live = useDeviceDataStore.getState().deviceMap[devId];
      const data = { ...(live?.points ?? {}), alarmList: live?.alarmList ?? [] };
      setState({ data: parseData(data, (ds as any)?.parser), loading: false, error: null });
      return;
    }
    // 引用数据集：拉数据集定义（数据集自身 parser 在数据源层已执行）
    if (ds.dataSetId) {
      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;
      setState((s) => ({ ...s, loading: true }));
      try {
        const all = await fetchDataSources();
        const set = all.find((d) => d.id === ds.dataSetId);
        if (!set) {
          // 数据集被删除/不存在：回退内联配置（若有 type+config），否则空数据
          if (ds.type && ds.type !== 'static') {
            const raw = await fetchDataSource(ds, abort.signal);
            if (!abort.signal.aborted) apply(raw, (ds as any)?.parser);
          } else {
            setState((s) => ({ ...s, loading: false, error: new Error('数据集不存在或已被删除') }));
          }
          return;
        }
        dsDefRef.current = set;
        // websocket 数据集：由 useEffect 的流式订阅处理，这里不拉取
        if (set.type === 'websocket') {
          setState((s) => ({ ...s, loading: false }));
          return;
        }
        // 其他类型：拉取 + 数据集解析 + 组件解析叠加
        const raw = await fetchDataSource({ type: set.type, config: set.config }, abort.signal);
        if (!abort.signal.aborted) applyWithSetParser(raw);
      } catch (e: any) {
        if (!abort.signal.aborted) {
          setState((s) => ({ ...s, loading: false, error: e }));
        }
      }
      return;
    }
    if (ds.type === 'static') {
      setState({ data: parseData(readStaticData(ds), (ds as any)?.parser), loading: false, error: null });
      return;
    }
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setState((s) => ({ ...s, loading: true }));
    try {
      const raw = await fetchDataSource(ds, abort.signal);
      if (!abort.signal.aborted) apply(raw);
    } catch (e: any) {
      if (!abort.signal.aborted && !(e?.name === 'CanceledError')) {
        setState((s) => ({ ...s, loading: false, error: e }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsKey]);

  runRef.current = run;

  useEffect(() => {
    // DW-P0-3：发布快照静态化数据优先（数据已冻结；不建立轮询/实时订阅）
    const frozenKey = component?.id ? `widget:${component.id}` : '';
    const frozen = frozenKey ? useViewerStore.getState().staticData?.[frozenKey] : undefined;
    if (frozen !== undefined) {
      applyWithSetParser(frozen);
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    // 解析真实数据源类型：引用数据集看数据集定义，内联看组件
    const resolveType = async (): Promise<string | null> => {
      if (ds?.dataSetId) {
        // 已缓存则直接用；否则先拉定义
        if (dsDefRef.current) return dsDefRef.current.type;
        try {
          const all = await fetchDataSources();
          const set = all.find((d) => d.id === ds.dataSetId);
          if (set) {
            dsDefRef.current = set;
            return set.type;
          }
        } catch { /* 忽略，回退内联判断 */ }
        return ds.type || null;
      }
      return ds?.type || null;
    };

    let cancelled = false;
    (async () => {
      const realType = await resolveType();
      if (cancelled) return;
      // 真实类型为 websocket（无论引用数据集还是内联）：真流式订阅
      if (realType === 'websocket') {
        // 引用数据集：订阅地址用数据集定义；内联：用组件 ds
        const subDs = ds?.dataSetId && dsDefRef.current
          ? { type: 'websocket', config: dsDefRef.current.config }
          : ds;
        if (!subDs) return;
        const cleanup = subscribeDataSource(subDs, {
          onMessage: (raw) => applyWithSetParser(raw),
          onError: (e) => setState((s) => ({ ...s, loading: false, error: e })),
        });
        cleanupRef.current = cleanup;
        setState((s) => ({ ...s, loading: true }));
        return;
      }
      // device 类型：启动设备数据订阅 + 读当前快照（实时性由 deviceLive 订阅驱动；points 并入 alarmList 供告警指标用）
      if (realType === 'device') {
        ensureDeviceSocket();
        const devId = String((ds as any)?.config?.deviceId ?? '').trim();
        const live = useDeviceDataStore.getState().deviceMap[devId];
        const raw = { ...(live?.points ?? {}), alarmList: live?.alarmList ?? [] };
        apply(raw, (ds as any)?.parser);
        return;
      }
      // 其他：一次性拉取 + 轮询（引用模式轮询间隔用数据集定义，内联用组件）
      await run();
      const interval = (ds?.dataSetId ? dsDefRef.current?.refreshInterval : (ds as any)?.refreshInterval) as number | undefined;
      if (interval && interval > 0) {
        const timer = setInterval(() => runRef.current?.(), interval);
        cancelled = true; // 清理 timer 时不再 abort
        return () => {
          clearInterval(timer);
          abortRef.current?.abort();
        };
      }
      // 引用模式（非 ws）：每 5s 刷新数据集定义，检测 updatedAt/config 变化则重新拉取（改数据集定义全组件同步）
      if (ds?.dataSetId && realType !== 'websocket') {
        const defTimer = setInterval(async () => {
          try {
            const all = await fetchDataSources();
            const fresh = all.find((d) => d.id === ds.dataSetId);
            const old = dsDefRef.current;
            if (fresh && (!old || fresh.updatedAt !== old.updatedAt || JSON.stringify(fresh.config) !== JSON.stringify(old.config))) {
              dsDefRef.current = fresh;
              await runRef.current?.();
            }
          } catch { /* 忽略 */ }
        }, 5000);
        return () => clearInterval(defTimer);
      }
    })();
    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsKey]);

  const refresh = useCallback(async () => {
    // device 类型：实时性由 deviceLive 订阅保证，无需重连/轮询
    if (ds?.type === 'device') {
      const raw = useDeviceDataStore.getState().deviceMap[String((ds as any)?.config?.deviceId ?? '').trim()]?.points ?? {};
      setState({ data: parseData(raw, (ds as any)?.parser), loading: false, error: null });
      return;
    }
    // 引用数据集且为 ws：重连（用数据集定义）
    if (ds?.dataSetId && dsDefRef.current?.type === 'websocket') {
      cleanupRef.current?.();
      const cleanup = subscribeDataSource({ type: 'websocket', config: dsDefRef.current.config }, {
        onMessage: (raw) => applyWithSetParser(raw),
        onError: (e) => setState((s) => ({ ...s, loading: false, error: e })),
      });
      cleanupRef.current = cleanup;
      return;
    }
    // 内联 ws：重连
    if (ds?.type === 'websocket') {
      cleanupRef.current?.();
      const cleanup = subscribeDataSource(ds, {
        onMessage: (raw) => apply(raw),
        onError: (e) => setState((s) => ({ ...s, loading: false, error: e })),
      });
      cleanupRef.current = cleanup;
      return;
    }
    await runRef.current?.();
  }, [dsKey]);

  // 阶段3｜3.3：refresh 信号（未挂载 ws/轮询的组件由 run 重拉一次）
  const prevTickRef = useRef(refreshTick);
  useEffect(() => {
    if (refreshTick === prevTickRef.current) return;
    prevTickRef.current = refreshTick;
    if (refreshTick > 0 && !(ds?.type === 'websocket') && !ds?.dataSetId) {
      runRef.current?.();
    }
  }, [refreshTick, dsKey]);

  // device 类型：直接用响应式快照覆盖 data（数据同源、实时更新），仍走组件 parser 管线；points 并入 alarmList 供告警指标用
  const finalData = ds?.type === 'device'
    ? parseData({ ...(deviceLive?.points ?? {}), alarmList: deviceLive?.alarmList ?? [] }, (ds as any)?.parser)
    : state.data;

  return { ...state, data: finalData, refresh };
}
